import { useCallback, useMemo, useState } from 'react';
import type { PublishedBrief } from 'fym-shared/src/releases/types';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useNewBuild } from '../../appUpdate/appUpdateStore';
import { currentBuildId } from '../../appUpdate/versionCheck';
import { isNewerVersion, updateHandover, UpdateNotes, updatePrompt, UpdatePrompt } from './whatsNewRules';
import { readUpdateLater, writeUpdateLater } from './whatsNewStore';

/** A new build as the panel reads it. An old `version.json` with no version has the version ''. */
export interface RemoteBuild {
  version: string;
  buildId: string;
}

interface Input {
  enabled: boolean;
  running: string;
  locale: string;
  quietHome: boolean;
}

export interface UpdateAvailable {
  /** The new build that the app poll (`appUpdate/AppUpdateNotice`) found, or null. */
  remote: RemoteBuild | null;
  /** The notes of a newer version, when it has one entry or more. */
  notes: PublishedBrief | null;
  notesState: UpdateNotes;
  prompt: UpdatePrompt;
  /** Who shows the prompt: the plain toast of `AppUpdateNotice`, the panel, or null (wait). */
  handover: 'plain' | 'panel' | null;
  /** "Later" or the X: hides the prompt for this build id. */
  hide: () => void;
}

/**
 * The "update available" half of the web panel (spec 2026-10-20 What's new §7). It reads the new
 * build that the one app poll found (`appUpdate/appUpdateStore`) and, for a newer version, asks
 * `releases.get` for its notes. A build whose panel the person closed stays closed in this browser.
 */
export function useUpdateAvailable({ enabled, running, locale, quietHome }: Input): UpdateAvailable {
  const trpcAPI = useTRPCAPI();
  const newBuild = useNewBuild();
  const remote = useMemo<RemoteBuild | null>(
    () => (enabled && newBuild !== null ? { version: newBuild.version ?? '', buildId: newBuild.buildId } : null),
    [enabled, newBuild]
  );
  const [laterBuildId, setLaterBuildId] = useState(readUpdateLater);
  const newer = remote !== null && isNewerVersion(remote.version, running);

  const query = useTRPCQuery(
    trpcAPI.releases.get.queryOptions(
      { version: remote?.version ?? '', locale, surface: 'web' },
      { enabled: enabled && newer, retry: false }
    )
  );
  const answer = query.isSuccess ? (query.data ?? null) : null;
  const notes = answer !== null && answer.entries.length > 0 ? answer : null;
  let notesState: UpdateNotes = 'none';
  if (newer && !query.isSuccess && !query.isError) {
    notesState = 'pending';
  } else if (notes !== null) {
    notesState = 'some';
  }

  const prompt = updatePrompt({
    remote,
    ownVersion: running,
    ownBuildId: currentBuildId(),
    laterBuildId,
    notes: notesState,
    quietHome,
  });

  const buildId = remote?.buildId ?? null;
  const handover = updateHandover({ prompt, buildId, laterBuildId });
  const hide = useCallback(() => {
    if (buildId === null) {
      return;
    }
    writeUpdateLater(buildId);
    setLaterBuildId(buildId);
  }, [buildId]);

  return { remote, notes, notesState, prompt, handover, hide };
}
